/**
 * Frontend Configuration
 * 
 * IE-7: Environment-based configuration for API URLs
 * This replaces all hardcoded localhost URLs throughout the frontend.
 */

interface Config {
    apiUrl: string;
    aiServiceUrl: string;
    environment: 'development' | 'staging' | 'production';
    isDevelopment: boolean;
    isProduction: boolean;
}

// Use nullish coalescing to allow empty string (for relative URLs via CloudFront)
// Empty string means "use relative URLs" which works when all services
// are behind a unified domain with CloudFront Router
const config: Config = {
    apiUrl: process.env.REACT_APP_API_URL ?? 'http://localhost:3001',
    aiServiceUrl: process.env.REACT_APP_AI_URL ?? 'http://localhost:5001',
    environment: (process.env.REACT_APP_ENV as Config['environment']) || 'development',
    isDevelopment: process.env.NODE_ENV === 'development',
    isProduction: process.env.NODE_ENV === 'production',
};

// Validate configuration in production
// Empty string is valid (relative URLs via CloudFront), localhost is not
if (config.isProduction && config.apiUrl && config.apiUrl.includes('localhost')) {
    console.error('⚠️ WARNING: Production build contains localhost API URL');
}
if (config.isProduction && config.aiServiceUrl && config.aiServiceUrl.includes('localhost')) {
    console.error('⚠️ WARNING: Production build contains localhost AI service URL');
}

export default config;

// Export individual URLs for convenience
export const API_URL = config.apiUrl;
export const AI_SERVICE_URL = config.aiServiceUrl;

/**
 * Get the AI service endpoint URL.
 * When using CloudFront (apiUrl is empty), AI endpoints are at /ai/*
 * When using direct URLs (local dev), AI endpoints are at the root.
 * @param endpoint - The endpoint path without leading slash (e.g., 'generate', 'generate-payloads')
 */
export function getAiEndpoint(endpoint: string): string {
    if (config.aiServiceUrl === '' || config.aiServiceUrl === undefined) {
        // CloudFront deployment - AI service is at /ai/*
        return `/ai/${endpoint}`;
    }
    // Direct URL (local development)
    return `${config.aiServiceUrl}/${endpoint}`;
}
